/**
 * EL PLANO COMO CENTRAL DEL COTIZADOR (F1, 2026-08-22).
 *
 * Plantas como pestañas, herramientas compactas (Recinto · Trazar · Editar · Servicios),
 * el lienzo, y a un lado el PANEL DE CAPAS: cada recinto es una capa (ver/ocultar,
 * bloquear, seleccionar, abrir su habitación) y el recinto seleccionado tiene un
 * inspector con nombre, ancho, largo y ángulo tecleables. Teclear una medida le pone
 * candado (no la reajusta el imán) y crece hacia el lado opuesto al que está pegado.
 *
 * Nombrar un recinto al crearlo es darlo de alta como habitación (Teoría §6b): se elige
 * de una lista, escribir es la excepción.
 *
 * Deshacer: pila de hasta 30 parches inversos; el botón se ve 5 s tras cada acción.
 */

import { useEffect, useRef, useState } from 'react'
import Plano from './Plano.jsx'
import {
  NOMBRES_RECINTO, NOMBRES_CONTORNO, SERVICIOS, tipoPorNombre, idDeRecinto, plantasOrdenadas, esContorno,
  recintoRectangular, poligonoDeForma, aplicarMedida, normalizarAngulo, formaDesdePoligono, ladoPegado, LADOS
} from '../motor/plano.js'
import { CENTINELA_BORRAR } from '../datos/fusion.js'
import { area, caja } from '../motor/geometria.js'
import PaletaPlano, { etiquetaDe } from './PaletaPlano.jsx'
import { TIPOS_ELECTRICO, idContador } from './Paleta.jsx'

const ICONO = {
  ojo: <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Zm10 3a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />,
  ojoCerrado: <path d="M3 3l18 18M10.6 10.6A3 3 0 0 0 13.4 13.4M9.9 5.2A10 10 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.2 3.9M6.4 6.4A17 17 0 0 0 2 12s3.5 7 10 7a9.5 9.5 0 0 0 4-.9" />,
  candado: <path d="M7 11V8a5 5 0 0 1 10 0v3M6 11h12v10H6z" />,
  candadoAbierto: <path d="M7 11V8a5 5 0 0 1 9.6-2M6 11h12v10H6z" />,
  chev: <path d="M9 6l6 6-6 6" />,
  basura: <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
}
const Icono = ({ n }) => <svg viewBox="0 0 24 24" aria-hidden="true">{ICONO[n]}</svg>

export default function VistaPlano({
  plano, habitaciones = [], titulo, migaja, resumenDe = () => '',
  onParche, onRecintoNuevo, onAbrirRecinto, onRevision, onTerminar,
  atras, Barra, Sync,
  /* F3 — la paleta en el plano (solo con cotización abierta) */
  cotId = null, objetos = {}, onObjeto = () => {}, fotosDe = () => [], onFotos = () => {},
  notaDe = () => '', onNota = () => {}, productosPorRubro = {}, onRecalcular,
  diagnostico = {}, onDiagnostico = () => {}, onAbrirMotor = () => {}
}) {
  const plantas = plantasOrdenadas(plano)
  const [planta, setPlanta] = useState(plantas[0]?.id || 'pb')
  const hayRecintos = Object.keys(plano?.recintos || {}).length > 0
  const [herramienta, setHerramienta] = useState(hayRecintos ? 'ver' : 'recinto')
  const [trazo, setTrazo] = useState([])
  const [activo, setActivo] = useState(null)
  const [nombrando, setNombrando] = useState(hayRecintos ? null : 'rect')      // 'trazo' | 'rect' | null
  const [nombre, setNombre] = useState('')
  const [menuServicios, setMenuServicios] = useState(false)
  const [enfocar, setEnfocar] = useState(null)
  const [campos, setCampos] = useState({ nombre: '', ancho: '', largo: '', ang: '' })
  const pila = useRef([])
  const [puedeDeshacer, setPuedeDeshacer] = useState(false)
  const temporizador = useRef(null)
  const [rubro, setRubro] = useState('electrico')        // la capa activa (F3)
  const [colocando, setColocando] = useState(null)       // 'rubro:clave:estado' mientras se coloca en el plano
  const recalculo = useRef(null)
  const recalcularRef = useRef(onRecalcular)
  recalcularRef.current = onRecalcular            // siempre la versión con la cotización más fresca
  /** Tras capturar, las partidas del recinto se recalculan solas (idempotente, E4b). */
  const recalcular = recintoId => {
    if (!onRecalcular || !recintoId) return
    clearTimeout(recalculo.current)
    recalculo.current = setTimeout(() => recalcularRef.current?.(recintoId), 900)
  }
  useEffect(() => () => clearTimeout(recalculo.current), [])

  const mostrarDeshacer = () => {
    setPuedeDeshacer(true)
    clearTimeout(temporizador.current)
    temporizador.current = setTimeout(() => setPuedeDeshacer(false), 5000)
  }
  useEffect(() => () => clearTimeout(temporizador.current), [])

  const aplicar = (parche, inverso) => {
    onParche(parche)
    if (inverso) { pila.current = [...pila.current.slice(-29), inverso]; mostrarDeshacer() }
  }
  const deshacer = () => {
    const inverso = pila.current.pop()
    if (!inverso) return
    onParche(inverso)
    if (!pila.current.length) setPuedeDeshacer(false)
  }

  const recintoActivo = plano?.recintos?.[activo] || null
  const recintosDePlanta = Object.values(plano?.recintos || {}).filter(r => r.planta === planta)
    .sort((a, b) => (esContorno(b) ? 1 : 0) - (esContorno(a) ? 1 : 0) || (a.orden ?? 0) - (b.orden ?? 0) || (a.actualizadoEn || '').localeCompare(b.actualizadoEn || ''))
  const otros = recintosDePlanta.filter(r => r.id !== activo && r.visible !== false)
  const habDe = id => habitaciones.find(h => h.id === id || h.recinto === id)
  const forma = recintoActivo?.forma?.tipo === 'rect' ? recintoActivo.forma : null

  /* El inspector copia lo del recinto activo cada vez que cambia. */
  useEffect(() => {
    setCampos({
      nombre: recintoActivo?.nombre || '',
      ancho: forma ? String(forma.ancho) : '', largo: forma ? String(forma.largo) : '',
      ang: forma ? String(Math.round((forma.ang || 0) * 10) / 10) : ''
    })
  }, [recintoActivo?.id, forma?.ancho, forma?.largo, forma?.ang, recintoActivo?.nombre]) // eslint-disable-line react-hooks/exhaustive-deps

  const seleccionar = (id, { abrirEditar = false, centrar = false } = {}) => {
    setActivo(id)
    if (id && abrirEditar && herramienta !== 'editar') setHerramienta('editar')
    if (id && centrar) setEnfocar({ id, n: (enfocar?.n || 0) + 1 })
  }

  /* ---------- Crear ---------- */

  const darDeAlta = recinto => {
    aplicar({ recintos: { [recinto.id]: recinto } }, { recintos: { [recinto.id]: CENTINELA_BORRAR } })
    if (!esContorno(recinto)) onRecintoNuevo(recinto)      // el terreno y la construcción no se cotizan
    setTrazo([]); setNombrando(null); setNombre('')
    setActivo(recinto.id); setHerramienta('editar')
    setEnfocar({ id: recinto.id, n: (enfocar?.n || 0) + 1 })
  }

  const cerrarTrazo = nombreElegido => {
    const n = (nombreElegido || nombre).trim()
    if (!n || trazo.length < 3) return
    const id = idDeRecinto(n, plano?.recintos || {})
    const cuadrada = formaDesdePoligono(trazo)
    const recinto = cuadrada
      ? recintoRectangular({ id, planta, nombre: n, ...cuadrada })
      : { id, planta, nombre: n, tipo: tipoPorNombre(n), poligono: trazo, cerrado: true, actualizadoEn: new Date().toISOString() }
    darDeAlta(recinto)
  }

  /**
   * Un rectángulo de 3 × 3 a la derecha de lo que ya hay (con el muro de por medio), o en el
   * origen. Los contornos nacen grandes y centrados sobre lo que ya hay: terreno 10 × 15,
   * construcción 8 × 10.
   */
  const crearRectangulo = nombreElegido => {
    const n = (nombreElegido || nombre).trim()
    if (!n) return
    const id = idDeRecinto(n, plano?.recintos || {})
    const tipo = tipoPorNombre(n)
    const pts = recintosDePlanta.flatMap(r => r.poligono || [])
    const b = pts.length ? caja(pts) : null
    if (tipo === 'predio' || tipo === 'construccion') {
      const [ancho, largo] = tipo === 'predio' ? [10, 15] : [8, 10]
      const cx = b ? (b.x0 + b.x1) / 2 : ancho / 2, cy = b ? (b.y0 + b.y1) / 2 : largo / 2
      darDeAlta(recintoRectangular({ id, planta, nombre: n, cx, cy, ancho, largo }))
      return
    }
    let cx = 2.5, cy = 2.5
    if (b) { cx = b.x1 + 0.15 + 1.5; cy = b.y0 + 1.5 }
    darDeAlta(recintoRectangular({ id, planta, nombre: n, cx, cy }))
  }

  const agregarPlanta = () => {
    const n = plantas.length
    const id = n === 1 ? 'pa' : `p${n}`
    const nombrePlanta = n === 1 ? 'Planta alta' : `Nivel ${n + 1}`
    aplicar({ plantas: { [id]: { id, nombre: nombrePlanta, orden: n } } }, { plantas: { [id]: CENTINELA_BORRAR } })
    setPlanta(id); setActivo(null); setHerramienta('recinto')
  }

  /* ---------- Editar desde el inspector ---------- */

  const parcheRecinto = (id, cambio, inverso) => aplicar({ recintos: { [id]: cambio } }, { recintos: { [id]: inverso } })

  const teclearMedida = (campo, texto) => {
    if (!recintoActivo || !forma) return
    const v = parseFloat(String(texto).replace(',', '.'))
    if (!isFinite(v)) { setCampos(c => ({ ...c, [campo]: campo === 'ang' ? String(forma.ang || 0) : String(forma[campo]) })); return }
    let f
    if (campo === 'ang') f = { ...forma, ang: Math.round(normalizarAngulo(v) * 10) / 10 }
    else f = aplicarMedida(forma, campo, v, otros)
    if (f[campo] === forma[campo] && campo !== 'ang') return
    const candados = { ...(recintoActivo.candados || {}), [campo]: true }
    parcheRecinto(recintoActivo.id, { forma: f, poligono: poligonoDeForma(f), candados },
      { forma, poligono: recintoActivo.poligono, candados: recintoActivo.candados || CENTINELA_BORRAR })
  }
  const quitarCandado = campo => {
    if (!recintoActivo) return
    const c = { ...(recintoActivo.candados || {}) }; delete c[campo]
    parcheRecinto(recintoActivo.id, { candados: Object.keys(c).length ? c : CENTINELA_BORRAR }, { candados: recintoActivo.candados })
  }
  const renombrar = () => {
    const n = campos.nombre.trim()
    if (!recintoActivo || !n || n === recintoActivo.nombre) return
    parcheRecinto(recintoActivo.id, { nombre: n, tipo: tipoPorNombre(n) }, { nombre: recintoActivo.nombre, tipo: recintoActivo.tipo })
  }
  const alternar = (r, campo) => parcheRecinto(r.id, { [campo]: !r[campo] }, { [campo]: !!r[campo] })
  const eliminar = r => {
    parcheRecinto(r.id, CENTINELA_BORRAR, r)
    if (activo === r.id) setActivo(null)
  }
  const cuadrar = () => {
    if (!recintoActivo || forma) return
    const f = formaDesdePoligono(recintoActivo.poligono)
    if (!f) return
    parcheRecinto(recintoActivo.id, { forma: f, poligono: poligonoDeForma(f) }, { forma: CENTINELA_BORRAR, poligono: recintoActivo.poligono })
  }
  const pegado = forma ? ladoPegado(forma, otros) : null

  /* ---------- F3: colocar objetos en el plano ---------- */

  const idsDePlanta = new Set(recintosDePlanta.map(r => r.id))
  const marcadores = Object.values(objetos).flatMap(o => (o.posiciones || [])
    .filter(() => idsDePlanta.has(o.recinto))
    .map((p, i) => ({ id: `${o.id}-${i}`, objetoId: o.id, indice: i, recinto: o.recinto, x: p.x, y: p.y, etiqueta: etiquetaDe(o), rubro: o.rubro, estado: o.estado, activo: o.rubro === rubro })))

  /** Tocar un marcador lo quita: baja el contador y borra esa posición (q 0 → el objeto se va). */
  const quitarMarcador = (objetoId, i) => {
    const o = objetos[objetoId]
    if (!o) return
    const posiciones = (o.posiciones || []).filter((_, k) => k !== i)
    const q = Math.max(0, (o.q || 0) - 1)
    onObjeto(objetoId, q > 0 ? { ...o, q, posiciones } : null)
    recalcular(o.recinto)
  }

  const colocar = (recintoId, p) => {
    if (!colocando) return
    const [r, clave, estado] = colocando.split(':')
    let id, base
    if (r === 'electrico') {
      id = idContador(recintoId, clave, estado)
      base = { id, recinto: recintoId, rubro: 'electrico', tipo: clave, estado, q: 0, sku: TIPOS_ELECTRICO.find(t => t.id === clave)?.sku || null }
    } else {
      id = `${recintoId}__${clave}__propuesto`
      base = { id, recinto: recintoId, rubro: r, tipo: 'producto', sku: clave, nombre: (productosPorRubro[r] || []).find(x => x.sku === clave)?.nombre, estado: 'propuesto', q: 0 }
    }
    const previo = objetos[id] || { ...base, capturadoEn: new Date().toISOString() }
    onObjeto(id, { ...previo, q: (previo.q || 0) + 1, posiciones: [...(previo.posiciones || []), p] })
    recalcular(recintoId)
  }
  const objetoEnRecinto = (id, valor) => { onObjeto(id, valor); recalcular(recintoActivo?.id) }
  const cotizables = Object.values(plano?.recintos || {}).filter(r => !esContorno(r))
  const resumenGuia = {
    recintosTotal: cotizables.length,
    recintosConCaptura: cotizables.filter(r => Object.values(objetos).some(o => o.recinto === r.id)).length,
    hallazgos: Object.values(objetos).filter(o => o.tipo === 'hallazgo').length
  }

  const elegirHerramienta = h => {
    setHerramienta(h); setMenuServicios(false); setColocando(null)
    if (h === 'trazar') { setActivo(null); setTrazo([]) }
    if (h === 'recinto') setNombrando('rect')
    else if (nombrando === 'rect') setNombrando(null)
  }

  const nombrePlanta = plantas.find(p => p.id === planta)?.nombre || 'esta planta'
  const pista = () => {
    if (nombrando) return null
    if (colocando) return recintoActivo ? `Colocando en ${recintoActivo.nombre}: toca el plano por cada uno. Para cambiar de recinto, elígelo en las capas.` : 'Elige un recinto en las capas para colocar.'
    if (herramienta === 'recinto') return 'Elige el nombre del recinto y aparece un cuadro de 3 × 3 m listo para acomodar.'
    if (herramienta === 'trazar') {
      if (trazo.length === 0) return 'Toca las esquinas del recinto, una por una. Si pega con otro, el imán las junta.'
      if (trazo.length < 3) return `${trazo.length} ${trazo.length === 1 ? 'esquina' : 'esquinas'}. Faltan ${3 - trazo.length} para poder cerrar.`
      return <><b>{area(trazo).toFixed(1)}</b> m² — toca la primera esquina para cerrar, o «Cerrar el recinto».</>
    }
    if (herramienta === 'mano') return 'Arrastra con un dedo para mover el plano; pellizca para acercar.'
    if (herramienta === 'editar') {
      if (!recintoActivo) return 'Elige un recinto en las capas; el dedo solo mueve, estira o gira el que está elegido.'
      if (recintoActivo.bloqueado) return `${recintoActivo.nombre} está bloqueado. Quita el candado en el panel para editarlo.`
      return forma ? `Arrastra ${recintoActivo.nombre} para moverlo, sus paredes para estirarlo, la perilla para girarlo. Cerca de otro recinto se pega dejando el muro.` : `Arrastra ${recintoActivo.nombre} o una de sus esquinas.`
    }
    if (herramienta.startsWith('servicio:')) return `Toca en el plano dónde está ${SERVICIOS.find(s => `servicio:${s.id}` === herramienta)?.nombre.toLowerCase()}.`
    return 'Elige un recinto en las capas para editarlo. Dos dedos mueven y acercan el plano.'
  }

  return (
    <div className="app plano-central">
      <Barra titulo={titulo} migaja={migaja} atras={atras} acciones={Sync} />
      <main className="plano-main">
        <div className="plano-col">
          {/* Plantas */}
          <div className="etapas" style={{ marginBottom: 'var(--e3)' }}>
            {plantas.map(p => (
              <button type="button" key={p.id} className={`et ${planta === p.id ? 'on1' : ''}`}
                onClick={() => { setPlanta(p.id); setActivo(null); setTrazo([]); setNombrando(null) }}>{p.nombre}</button>
            ))}
            {plantas.length < 4 && <button type="button" className="et" onClick={agregarPlanta}>+ Planta</button>}
          </div>

          {/* Herramientas */}
          <div className="fila-pills herramientas" style={{ marginBottom: 'var(--e3)' }}>
            <button type="button" className={`ib sm ${herramienta === 'recinto' ? 'pri' : ''}`} onClick={() => elegirHerramienta('recinto')}>+ Recinto</button>
            <button type="button" className={`ib sm ${herramienta === 'trazar' ? 'pri' : ''}`} onClick={() => elegirHerramienta('trazar')}>Trazar</button>
            <button type="button" className={`ib sm ${herramienta === 'editar' ? 'pri' : ''}`} disabled={!recintosDePlanta.length} onClick={() => elegirHerramienta('editar')}>Editar</button>
            <button type="button" className={`ib sm ${herramienta === 'mano' ? 'pri' : ''}`} onClick={() => elegirHerramienta('mano')} title="Arrastra para mover el plano">Mover plano</button>
            <button type="button" className={`ib sm ${herramienta.startsWith('servicio:') ? 'pri' : ''}`} onClick={() => setMenuServicios(v => !v)} aria-expanded={menuServicios}>
              {herramienta.startsWith('servicio:') ? SERVICIOS.find(s => `servicio:${s.id}` === herramienta)?.nombre : 'Servicios'} ▾
            </button>
          </div>
          {menuServicios && (
            <div className="fila-pills menu-servicios" style={{ marginBottom: 'var(--e3)' }}>
              {SERVICIOS.map(s => (
                <button type="button" key={s.id} className={`ib sm ${herramienta === `servicio:${s.id}` ? 'pri' : ''}`}
                  onClick={() => elegirHerramienta(`servicio:${s.id}`)}>{s.nombre}</button>
              ))}
            </div>
          )}

          <Plano plano={plano} planta={planta} recintoActivo={activo}
            herramienta={colocando ? 'colocar' : herramienta === 'recinto' ? 'ver' : herramienta}
            trazo={trazo} setTrazo={setTrazo} onParche={aplicar} enfocar={enfocar}
            marcadores={marcadores} onColocar={colocar} onQuitarMarcador={quitarMarcador}
            onCerrarTrazo={() => { if (trazo.length >= 3) setNombrando('trazo') }}
            onSeleccionar={id => seleccionar(id, { abrirEditar: herramienta === 'ver' && !!id && !colocando })} />

          {/* La barra bajo el lienzo: la pista y, en la MISMA barra, lo que toca hacer
              (cerrar el trazo, deshacer) — Juan no encontraba «Cerrar el recinto» abajo. */}
          <div className="cota-jardin pista">
            <span>{pista()}</span>
            {herramienta === 'trazar' && trazo.length > 0 && !nombrando && (
              <span className="pista-acciones">
                <button type="button" className="ib sm" onClick={() => setTrazo([])}>Cancelar trazo</button>
                <button type="button" className="ib sm" onClick={() => setTrazo(trazo.slice(0, -1))}>Quitar esquina</button>
                <button type="button" className="ib sm pri" disabled={trazo.length < 3} onClick={() => setNombrando('trazo')}>Cerrar el recinto</button>
              </span>
            )}
            {puedeDeshacer && <button type="button" className="discreto" onClick={deshacer}>Deshacer</button>}
          </div>

          {/* Nombrar = dar de alta la habitación. */}
          {nombrando && (
            <div className="card captura" ref={el => { if (el && !el.dataset.visto) { el.dataset.visto = '1'; el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }) } }}>
              <div className="t" style={{ marginBottom: 4 }}>{nombrando === 'rect' ? '¿Qué recinto vas a acomodar?' : 'El trazo está listo'}</div>
              <div className="s" style={{ marginBottom: 10 }}>{nombrando === 'rect' ? 'Aparece un cuadro de 3 × 3 m que luego acomodas.' : 'Elige el nombre y queda guardado como capa.'}</div>
              <div className="fila-pills" style={{ marginBottom: 8 }}>
                {NOMBRES_CONTORNO.map(n => (
                  <button type="button" key={n} className="ib sm contorno" onClick={() => (nombrando === 'rect' ? crearRectangulo(n) : cerrarTrazo(n))}>{n}</button>
                ))}
              </div>
              <div className="fila-pills">
                {NOMBRES_RECINTO.map(n => (
                  <button type="button" key={n} className="ib sm" onClick={() => (nombrando === 'rect' ? crearRectangulo(n) : cerrarTrazo(n))}>{n}</button>
                ))}
              </div>
              <label className="f" style={{ margin: 0 }}>
                <span>Otro nombre</span>
                <input value={nombre} placeholder="Vestíbulo, bodega…" onChange={e => setNombre(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') (nombrando === 'rect' ? crearRectangulo() : cerrarTrazo()) }} />
              </label>
              <div className="captura-acc">
                <button type="button" className="ib" onClick={() => { setNombrando(null); if (herramienta === 'recinto') setHerramienta(recintosDePlanta.length ? 'ver' : 'recinto') }}>
                  {nombrando === 'rect' ? 'Cancelar' : 'Volver al trazo'}
                </button>
                <button type="button" className="ib pri" disabled={!nombre.trim()} onClick={() => (nombrando === 'rect' ? crearRectangulo() : cerrarTrazo())}>Dar de alta</button>
              </div>
            </div>
          )}
        </div>

        <aside className="plano-panel">
          <div className="sec" style={{ marginTop: 0 }}>Capas · {nombrePlanta.toLowerCase()}</div>
          {recintosDePlanta.length === 0 && (
            <div className="empty chico">Todavía no hay recintos en {nombrePlanta.toLowerCase()}. Empieza por el terreno o por el cuarto donde estás.</div>
          )}
          <div className="capas" role="list">
            {recintosDePlanta.map(r => {
              const h = habDe(r.id)
              const res = resumenDe(r.id)
              return (
                <div key={r.id} role="listitem" className={`capa ${activo === r.id ? 'activa' : ''} ${r.visible === false ? 'oculta' : ''}`}>
                  <button type="button" className="icono" aria-label={r.visible === false ? `Mostrar ${r.nombre}` : `Ocultar ${r.nombre}`}
                    aria-pressed={r.visible !== false} onClick={() => parcheRecinto(r.id, { visible: r.visible === false }, { visible: r.visible !== false })}>
                    <Icono n={r.visible === false ? 'ojoCerrado' : 'ojo'} />
                  </button>
                  <button type="button" className="nombre" onClick={() => seleccionar(r.id, { abrirEditar: true, centrar: true })}>
                    <b>{r.nombre}</b>
                    <span>{area(r.poligono).toFixed(1)} m²{esContorno(r) ? <> · <i>contorno</i></> : res ? ` · ${res}` : (h ? '' : ' · sin habitación')}</span>
                  </button>
                  <button type="button" className="icono" aria-label={r.bloqueado ? `Desbloquear ${r.nombre}` : `Bloquear ${r.nombre}`}
                    aria-pressed={!!r.bloqueado} onClick={() => alternar(r, 'bloqueado')}>
                    <Icono n={r.bloqueado ? 'candado' : 'candadoAbierto'} />
                  </button>
                  <button type="button" className="icono borrar" aria-label={`Eliminar ${r.nombre}`} title="Eliminar (se puede deshacer 5 s)" onClick={() => eliminar(r)}>
                    <Icono n="basura" />
                  </button>
                  {h && (
                    <button type="button" className="icono" aria-label={`Abrir ${r.nombre}`} onClick={() => onAbrirRecinto(r.id)}>
                      <Icono n="chev" />
                    </button>
                  )}
                </div>
              )
            })}
          </div>

          {recintoActivo && (
            <div className="card inspector-card">
              <label className="f" style={{ marginBottom: 8 }}>
                <span>Nombre</span>
                <input value={campos.nombre} onChange={e => setCampos(c => ({ ...c, nombre: e.target.value }))}
                  onBlur={renombrar} onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }} />
              </label>
              {forma ? (
                <>
                  <div className="inspector">
                    {[['ancho', 'Ancho (m)'], ['largo', 'Largo (m)'], ['ang', 'Ángulo (°)']].map(([campo, etiqueta]) => (
                      <label className="f" key={campo} style={{ margin: 0 }}>
                        <span>{etiqueta}{recintoActivo.candados?.[campo] && (
                          <button type="button" className="candado" title="Medida fija: el imán no la cambia. Toca para soltarla." aria-label={`Soltar ${etiqueta}`} onClick={() => quitarCandado(campo)}>🔒</button>
                        )}</span>
                        <input inputMode="decimal" value={campos[campo]} disabled={!!recintoActivo.bloqueado}
                          onChange={e => setCampos(c => ({ ...c, [campo]: e.target.value }))}
                          onBlur={e => teclearMedida(campo, e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }} />
                      </label>
                    ))}
                  </div>
                  <div className="s" style={{ marginTop: 8 }}>
                    {pegado === null ? 'Sin pared pegada: una medida tecleada crece desde el centro.' : `Pared ${LADOS[pegado]} pegada a otro recinto: se queda; crece la opuesta.`}
                  </div>
                </>
              ) : (
                <div className="s">
                  Trazado a mano · {recintoActivo.poligono?.length || 0} esquinas · {area(recintoActivo.poligono || []).toFixed(1)} m²
                  {formaDesdePoligono(recintoActivo.poligono || []) && (
                    <> · <button type="button" className="discreto" onClick={cuadrar}>Cuadrar a rectángulo</button></>
                  )}
                </div>
              )}
              <div className="captura-acc" style={{ marginTop: 10 }}>
                <button type="button" className="ib sm peligro" onClick={() => eliminar(recintoActivo)}>Eliminar {recintoActivo.nombre}</button>
                {habDe(recintoActivo.id) && (
                  <button type="button" className="ib sm pri" onClick={() => onAbrirRecinto(recintoActivo.id)}>Abrir habitación ›</button>
                )}
              </div>
            </div>
          )}

          {cotId && (
            <PaletaPlano rubro={rubro} setRubro={setRubro} cotId={cotId}
              recinto={recintoActivo && !esContorno(recintoActivo) ? recintoActivo : null}
              objetos={objetos} onObjeto={objetoEnRecinto}
              fotos={recintoActivo ? fotosDe(recintoActivo.id) : []} onFotos={lista => recintoActivo && onFotos(recintoActivo.id, lista)}
              nota={recintoActivo ? notaDe(recintoActivo.id) : ''} onNota={texto => recintoActivo && onNota(recintoActivo.id, texto)}
              productosPorRubro={productosPorRubro} colocando={colocando} setColocando={setColocando}
              onAbrirMotor={onAbrirMotor} diagnostico={diagnostico} onDiagnostico={onDiagnostico}
              resumenGuia={resumenGuia} onRevision={onRevision} />
          )}

          {onRevision && !cotId && (
            <>
              <div className="sec">Lo que exige instrumento</div>
              <div className="card tap" role="button" tabIndex={0} onClick={onRevision}
                onKeyDown={e => { if (e.key === 'Enter') onRevision() }}>
                <div className="grow">
                  <div className="t">Revisión eléctrica — la hace el ingeniero</div>
                  <div className="s">Tablero abierto, megóhmetro y termografía: trabajo sobre instalación energizada.</div>
                </div>
                <div className="chev">›</div>
              </div>
            </>
          )}
        </aside>
      </main>
      {onTerminar && (
        <div className="fab"><div className="in">
          <div className="cuadro">
            <div className="celda">
              <span className="k">Recintos</span>
              <span className="v">{Object.keys(plano?.recintos || {}).length}</span>
            </div>
            <div className="celda oculta-chica">
              <span className="k">Plantas</span>
              <span className="v">{plantas.length}</span>
            </div>
          </div>
          <button type="button" onClick={onTerminar}>Terminar por hoy</button>
        </div></div>
      )}
    </div>
  )
}
